<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRoute, useRouter } from 'vue-router';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import SubscriptionAPI from '../../../../api/subscription';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessageFormatter from 'shared/helpers/MessageFormatter';
import UpgradePlanModal from './UpgradePlanModal.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const formatMarkdown = text => {
  if (!text) return '';
  const formatter = new MessageFormatter(text);
  return formatter.formattedMessage;
};

const loading = ref(true);
const company = ref(null);
const currentPlan = ref(null);
const availablePlans = ref([]);
const showUpgradeModal = ref(false);
const selectedPlan = ref(null);

// Payment links for each plan
const paymentLinks = {
  PRO: 'https://app.takenos.com/pay/1f306beb-5972-4770-a831-3f91e6cd6372',
  BUSINESS: 'https://app.takenos.com/pay/69f3d159-6190-4b94-afe8-b30e45fc4ee3',
};

const fetchSubscriptionData = async () => {
  try {
    loading.value = true;
    const response = await SubscriptionAPI.get();

    if (response.data.success) {
      company.value = response.data.company;
      currentPlan.value = response.data.current_plan;
      availablePlans.value = response.data.available_plans || [];
    } else {
      useAlert(t('SUBSCRIPTION_SETTINGS.API.ERROR'));
    }
  } catch (error) {
    useAlert(t('SUBSCRIPTION_SETTINGS.API.ERROR'));
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await fetchSubscriptionData();

  // Load Chatwoot widget ONLY on subscription page
  window.chatwootSettings = {
    position: 'right',
    type: 'standard',
    launcherTitle: '',
  };

  const script = document.createElement('script');
  script.textContent = `
    (function(d,t) {
      var BASE_URL="https://crm.tulogica.com";
      var g=d.createElement(t),s=d.getElementsByTagName(t)[0];
      g.src=BASE_URL+"/packs/js/sdk.js";
      g.async = true;
      s.parentNode.insertBefore(g,s);
      g.onload=function(){
        window.chatwootSDK.run({
          websiteToken: 'D1iYTWWEgoSXnSeNEDUVAaBn',
          baseUrl: BASE_URL
        })
      }
    })(document,"script");
  `;
  document.body.appendChild(script);
});

onBeforeUnmount(() => {
  // Remove Chatwoot widget completely when leaving subscription page
  const chatwootHolder = document.querySelector('.chatwoot-widget-holder');
  if (chatwootHolder) {
    chatwootHolder.remove();
  }

  // Also remove Chatwoot SDK from window
  if (window.chatwootSDK) {
    delete window.chatwootSDK;
  }
  if (window.chatwootSettings) {
    delete window.chatwootSettings;
  }
});

const openPaymentWindow = planName => {
  // Normalizar el nombre del plan
  let normalizedName = planName.toUpperCase();
  if (normalizedName.includes('PLAN')) {
    normalizedName = normalizedName.replace('PLAN ', '').trim();
  }

  // eslint-disable-next-line no-console
  console.log('Normalized name:', normalizedName);

  const url = paymentLinks[normalizedName];

  if (url) {
    // Abrir en pestaña nueva en lugar de ventana emergente
    // Esto evita que se pierda cuando hagas clic fuera
    window.open(url, '_blank');
  } else {
    // eslint-disable-next-line no-console
    console.error('No payment URL found for plan:', planName);
    useAlert('No se encontró el enlace de pago para este plan', 'error');
  }
};

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatPrice = price => {
  if (price === null || price === undefined) return '$0';
  return `$${price}`;
};

const getPlanBadgeClass = planName => {
  const name = planName?.toUpperCase();
  if (name === 'FREE') return 'bg-n-slate-3 text-n-slate-11';
  if (name === 'BASIC') return 'bg-blue-50 text-blue-700';
  if (name === 'PRO') return 'bg-purple-50 text-purple-700';
  if (name === 'ENTERPRISE') return 'bg-amber-50 text-amber-700';
  return 'bg-n-slate-3 text-n-slate-11';
};

const isCurrentPlan = planId => {
  return currentPlan.value && currentPlan.value.plan_id === planId;
};

const isPlanDisabled = plan => {
  if (!currentPlan.value) return false;

  const currentPlanName = currentPlan.value.plan_name?.toUpperCase();
  const planName = plan.name?.toUpperCase();

  // Si estamos en BUSINESS, desactivar PLAN PRO y FREE
  if (currentPlanName === 'BUSINESS') {
    return planName === 'PLAN PRO' || planName === 'FREE';
  }

  // Si estamos en PLAN PRO, desactivar FREE
  if (currentPlanName === 'PLAN PRO') {
    return planName === 'FREE';
  }

  return false;
};

const expandedPlans = ref({});
const currentPlanExpanded = ref(false);

const togglePlanDescription = planId => {
  expandedPlans.value[planId] = !expandedPlans.value[planId];
};

const toggleCurrentPlanDescription = () => {
  currentPlanExpanded.value = !currentPlanExpanded.value;
};

const isPlanExpanded = planId => {
  return expandedPlans.value[planId] || false;
};

const truncateToLines = (text, maxLines = 5) => {
  if (!text) return '';
  const lines = text.split('\n');
  if (lines.length <= maxLines) return text;
  return lines.slice(0, maxLines).join('\n');
};

const getPlanDescription = plan => {
  return plan.description || 'Descripción del plan disponible próximamente.';
};

const isPlanExpired = () => {
  if (!currentPlan.value || !currentPlan.value.end_date) return false;
  const endDate = new Date(currentPlan.value.end_date);
  const today = new Date();
  // Comparar solo las fechas (sin hora)
  endDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  // El plan se considera vencido un día después de la fecha de vencimiento
  const expirationThreshold = new Date(endDate);
  expirationThreshold.setDate(expirationThreshold.getDate() + 1);
  return today >= expirationThreshold;
};

const openUpgradeModal = plan => {
  selectedPlan.value = plan;
  showUpgradeModal.value = true;
};

const handleUpgradeSuccess = async () => {
  showUpgradeModal.value = false;
  selectedPlan.value = null;
  await fetchSubscriptionData();
};

const goToPaymentHistory = () => {
  router.push({
    name: 'payment_history',
    params: { accountId: route.params.accountId },
  });
};
</script>

<template>
  <div class="grid max-w-4xl ltr:mr-auto rtl:ml-auto">
    <div class="flex items-center justify-between mb-4">
      <BaseSettingsHeader
        :title="$t('SUBSCRIPTION_SETTINGS.TITLE')"
        :description="$t('SUBSCRIPTION_SETTINGS.DESCRIPTION')"
      />
      <div class="flex items-center gap-2">
        <!-- Support Button -->
        <a
          href="https://api.whatsapp.com/send?phone=59160785633&text=Hola%2C%20requiero%20confirmaci%C3%B3n%20de%20mi%20pago%20y%20la%20habilitaci%C3%B3n%20de%20mi%20cuenta.%20Quedo%20atento%2C%20gracias."
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
          :title="$t('SUBSCRIPTION_SETTINGS.SUPPORT.TITLE')"
        >
          <Icon name="i-lucide-message-circle" class="w-4 h-4" />
          {{ $t('SUBSCRIPTION_SETTINGS.SUPPORT.BUTTON') }}
        </a>

        <!-- Payment History Button -->
        <button
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:bg-n-brand-600 transition-colors"
          @click="goToPaymentHistory"
        >
          <Icon icon="i-lucide-receipt" class="w-4 h-4" />
          {{ $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.TITLE') }}
        </button>
      </div>
    </div>

    <woot-loading-state v-if="loading" />

    <template v-else>
      <!-- Current Subscription -->
      <SectionLayout
        :title="$t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.TITLE')"
        :description="$t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.DESCRIPTION')"
        class="!pt-0"
      >
        <div
          v-if="currentPlan"
          class="p-6 border rounded-xl"
          :class="
            isPlanExpired()
              ? 'border-red-300 bg-red-50'
              : 'border-n-slate-6 bg-n-slate-1'
          "
        >
          <div class="flex items-start justify-between mb-4">
            <div>
              <div class="flex items-center gap-2 mb-2">
                <h3 class="text-lg font-semibold text-n-slate-12">
                  {{ currentPlan.plan_name }}
                </h3>
                <span
                  class="px-2 py-1 text-xs font-medium rounded-md"
                  :class="
                    isPlanExpired()
                      ? 'bg-red-100 text-red-700'
                      : getPlanBadgeClass(currentPlan.plan_name)
                  "
                >
                  {{
                    isPlanExpired()
                      ? $t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.EXPIRED')
                      : $t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.ACTIVE')
                  }}
                </span>
              </div>
              <p class="text-sm text-n-slate-11">
                {{
                  `${$t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.MESSAGE_LIMIT')}: -`
                }}
              </p>
              <p
                v-if="
                  currentPlan.plan_name.toUpperCase() !== 'FREE' &&
                  currentPlan.end_date
                "
                class="text-sm mt-1"
                :class="
                  isPlanExpired()
                    ? 'text-red-700 font-medium'
                    : 'text-n-slate-11'
                "
              >
                {{
                  isPlanExpired()
                    ? `Venció el: ${formatDate(currentPlan.end_date)}`
                    : `${$t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.EXPIRES_ON')}: ${formatDate(currentPlan.end_date)}`
                }}
              </p>
            </div>
            <div class="text-right">
              <p class="text-2xl font-bold text-n-slate-12">
                {{ formatPrice(currentPlan.price_month) }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ $t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.PER_MONTH') }}
              </p>
            </div>
          </div>

          <!-- Descripción del plan actual -->
          <div
            v-if="currentPlan.plan_description"
            class="mt-4 p-4 bg-n-slate-2 rounded-lg border border-n-slate-6"
          >
            <div class="flex items-start gap-2">
              <Icon
                name="i-lucide-info"
                class="w-4 h-4 text-n-brand mt-0.5 flex-shrink-0"
              />
              <div class="flex-1">
                <div class="flex items-center justify-between mb-2">
                  <p class="text-xs font-medium text-n-slate-12">
                    {{ $t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.DESCRIPTION') }}
                  </p>
                  <button
                    v-if="currentPlan.plan_description.split('\n').length > 5"
                    class="text-xs text-n-brand hover:underline flex items-center gap-1"
                    @click="toggleCurrentPlanDescription"
                  >
                    {{
                      currentPlanExpanded
                        ? $t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.SEE_LESS')
                        : $t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.SEE_MORE')
                    }}
                    <Icon
                      :name="
                        currentPlanExpanded
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                      class="w-3 h-3"
                    />
                  </button>
                </div>
                <div
                  v-dompurify-html="
                    formatMarkdown(
                      currentPlanExpanded
                        ? currentPlan.plan_description
                        : truncateToLines(currentPlan.plan_description, 5)
                    )
                  "
                  class="text-sm text-n-slate-11 leading-relaxed prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_p]:mb-2 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-medium [&_h3]:mb-1 [&_strong]:font-semibold [&_em]:italic [&_code]:bg-n-slate-3 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_a]:text-n-brand [&_a]:underline"
                />
              </div>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4 pt-4 border-t border-n-slate-6">
            <div>
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.START_DATE') }}
              </p>
              <p class="text-sm font-medium text-n-slate-12">
                {{ formatDate(currentPlan.start_date) }}
              </p>
            </div>
            <div>
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.BILLING_CYCLE') }}
              </p>
              <p class="text-sm font-medium text-n-slate-12 capitalize">
                {{ currentPlan.billing_cycle }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-else
          class="p-6 border border-n-slate-6 rounded-xl bg-n-slate-1 text-center"
        >
          <Icon
            icon="i-lucide-alert-circle"
            class="w-12 h-12 mx-auto mb-3 text-n-slate-9"
          />
          <p class="text-sm text-n-slate-11">
            {{ $t('SUBSCRIPTION_SETTINGS.CURRENT_PLAN.NO_PLAN') }}
          </p>
        </div>
      </SectionLayout>

      <!-- Available Plans -->
      <SectionLayout
        with-border
        :title="$t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.TITLE')"
        :description="$t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.SUBTITLE')"
      >
        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="plan in availablePlans"
            :key="plan.id"
            class="relative p-6 border rounded-xl transition-all"
            :class="
              isCurrentPlan(plan.id)
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-slate-6 bg-n-slate-1 hover:border-n-slate-7'
            "
          >
            <div v-if="isCurrentPlan(plan.id)" class="absolute top-3 right-3">
              <Icon name="i-lucide-check-circle" class="w-5 h-5 text-n-brand" />
            </div>

            <div class="mb-4">
              <h4 class="text-lg font-semibold text-n-slate-12 mb-1">
                {{ plan.name }}
              </h4>
              <div class="flex items-baseline gap-1">
                <span class="text-3xl font-bold text-n-slate-12">
                  {{ formatPrice(plan.price_month) }}
                </span>
                <span class="text-sm text-n-slate-11">
                  {{ $t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.PER_MONTH') }}
                </span>
              </div>
            </div>

            <div class="space-y-3 mb-4">
              <div v-if="plan.price_year" class="flex items-start gap-2">
                <Icon
                  name="i-lucide-check"
                  class="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0"
                />
                <span class="text-sm text-n-slate-11">
                  {{ formatPrice(plan.price_year) }}
                  {{ $t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.PER_YEAR') }}
                </span>
              </div>

              <!-- Acordeón de descripción -->
              <div class="border-t border-n-slate-6 pt-3">
                <button
                  class="flex items-center justify-between w-full text-left hover:opacity-80 transition-opacity"
                  @click="togglePlanDescription(plan.id)"
                >
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      $t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.DESCRIPTION')
                    }}
                  </span>
                  <Icon
                    :name="
                      isPlanExpanded(plan.id)
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-chevron-down'
                    "
                    class="w-4 h-4 text-n-slate-11"
                  />
                </button>
                <div
                  v-if="isPlanExpanded(plan.id)"
                  v-dompurify-html="formatMarkdown(getPlanDescription(plan))"
                  class="mt-3 text-sm text-n-slate-11 leading-relaxed prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_p]:mb-2 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-medium [&_h3]:mb-1 [&_strong]:font-semibold [&_em]:italic [&_code]:bg-n-slate-3 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_a]:text-n-brand [&_a]:underline"
                />
              </div>
            </div>

            <!-- Payment Methods Section -->
            <div
              v-if="!isCurrentPlan(plan.id) || isPlanExpired()"
              class="mt-4 pt-4 border-t border-n-slate-6"
            >
              <!-- PayPal Button -->
              <button
                v-if="!isPlanDisabled(plan)"
                :disabled="isPlanDisabled(plan)"
                class="w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors mb-2 bg-n-brand text-white hover:bg-n-brand/90 flex items-center justify-center gap-2"
                @click="!isPlanDisabled(plan) && openUpgradeModal(plan)"
              >
                <Icon name="i-lucide-square" class="w-4 h-4" />
                {{ $t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.PAYPAL') }}
              </button>

              <!-- Credit Card Button -->
              <button
                v-if="!isPlanDisabled(plan)"
                :disabled="isPlanDisabled(plan)"
                class="w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-green-600 text-white hover:bg-green-700 flex items-center justify-center gap-2"
                @click="openPaymentWindow(plan.name)"
              >
                <Icon name="i-lucide-credit-card" class="w-4 h-4" />
                {{ $t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.CREDIT_CARD') }}
              </button>
            </div>

            <div
              v-else
              class="w-full px-4 py-2 text-sm font-medium text-center text-n-brand bg-n-brand/10 rounded-lg"
            >
              {{ $t('SUBSCRIPTION_SETTINGS.AVAILABLE_PLANS.CURRENT_PLAN') }}
            </div>
          </div>
        </div>
      </SectionLayout>
    </template>

    <UpgradePlanModal
      v-if="selectedPlan"
      :show="showUpgradeModal"
      :plan="selectedPlan"
      :account-id="Number(route.params.accountId)"
      @close="showUpgradeModal = false"
      @success="handleUpgradeSuccess"
    />
  </div>
</template>
